import { Bodies, DistanceJoint, type Body } from "fast-2d";
import { Camera } from "../render/Camera";
import type { Demo, Scene } from "./types";
import { makeWorld, floor, walls } from "./helpers";

/**
 * A wobbly jelly blob — a pressure-less soft body woven from a ring of small
 * circles held in shape by springy DistanceJoints: a stretchy skin around the
 * rim, spokes to a hub, and cross-braces for bounce. Drop it and it splats and
 * recovers; grab it and it squishes in your hand. The interactive face of the
 * roadmap's soft-body work — and the kind of squishy body Matter.js can only
 * fake with composites.
 */
export const jelly: Demo = {
	id: "jelly",
	title: "Jelly blob",
	blurb:
		"A squishy soft body: a ring of small circles laced together with springy DistanceJoints (skin + spokes + cross-braces) wobbles, splats, and bounces back into shape. Grab it and squish — it jiggles like real jelly.",
	category: "Playground & Toys",
	build(): Scene {
		const world = makeWorld();
		floor(world, 30);
		walls(world, 9, 24);

		const N = 18;
		const R = 1.7;
		const cx = 0;
		const cy = -6;
		// Nodes nearly touch around the rim so the skin reads as a sealed surface.
		const nodeR = R * Math.sin(Math.PI / N) * 0.92;

		const skin: Body[] = [];
		for (let i = 0; i < N; i++) {
			const a = (i / N) * Math.PI * 2;
			const node = world.add(
				Bodies.circle({
					position: [cx + Math.cos(a) * R, cy + Math.sin(a) * R],
					radius: nodeR,
					density: 1,
					restitution: 0.05,
					friction: 0.9,
					filter: { group: -1 }, // soft-body nodes never collide with each other
				}),
			);
			skin.push(node);
		}
		const hub = world.add(
			Bodies.circle({
				position: [cx, cy],
				radius: R * 0.28,
				density: 1,
				friction: 0.9,
				filter: { group: -1 },
			}),
		);

		const link = (a: Body, b: Body, frequency: number, damping: number): void => {
			world.addConstraint(new DistanceJoint(a, b, { frequency, dampingRatio: damping }));
		};

		for (let i = 0; i < N; i++) {
			const next = skin[(i + 1) % N]!;
			const node = skin[i]!;
			link(node, next, 8, 0.2); // stretchy skin
			link(node, skin[(i + 2) % N]!, 5, 0.3); // bending brace
			link(node, hub, 4, 0.4); // spoke keeps it plump
		}

		const onBeforeStep = (): void => {
			for (const n of skin) n.wake();
			hub.wake();
		};

		// Render the blob as a smooth glossy surface through the rim nodes.
		const draw = (ctx: CanvasRenderingContext2D, cam: Camera): void => {
			const hx = cam.worldToScreenX(hub.position.x);
			const hy = cam.worldToScreenY(hub.position.y);
			const pad = nodeR * cam.scale; // push the outline past the rim nodes to hide them
			const pts = skin.map((n) => {
				const x = cam.worldToScreenX(n.position.x);
				const y = cam.worldToScreenY(n.position.y);
				const dx = x - hx;
				const dy = y - hy;
				const len = Math.hypot(dx, dy) || 1;
				return { x: x + (dx / len) * pad, y: y + (dy / len) * pad };
			});
			ctx.beginPath();
			// Catmull-Rom-ish smoothing via midpoints + quadratic curves.
			let mid = { x: (pts[pts.length - 1]!.x + pts[0]!.x) / 2, y: (pts[pts.length - 1]!.y + pts[0]!.y) / 2 };
			ctx.moveTo(mid.x, mid.y);
			for (let i = 0; i < pts.length; i++) {
				const cur = pts[i]!;
				const nxt = pts[(i + 1) % pts.length]!;
				const nm = { x: (cur.x + nxt.x) / 2, y: (cur.y + nxt.y) / 2 };
				ctx.quadraticCurveTo(cur.x, cur.y, nm.x, nm.y);
				mid = nm;
			}
			ctx.closePath();

			const grad = ctx.createRadialGradient(hx - 18, hy - 22, 6, hx, hy, R * cam.scale * 1.3);
			grad.addColorStop(0, "rgba(120, 240, 200, 0.98)");
			grad.addColorStop(0.6, "rgba(40, 200, 170, 0.95)");
			grad.addColorStop(1, "rgba(20, 150, 140, 0.95)");
			ctx.fillStyle = grad;
			ctx.fill();
			ctx.strokeStyle = "rgba(220, 255, 245, 0.8)";
			ctx.lineWidth = 2;
			ctx.stroke();

			// A soft glossy highlight near the top-left.
			ctx.beginPath();
			ctx.ellipse(hx - 20, hy - 26, 14, 9, -0.5, 0, Math.PI * 2);
			ctx.fillStyle = "rgba(255, 255, 255, 0.35)";
			ctx.fill();
		};

		return {
			world,
			camera: new Camera(0, -3, 58),
			onBeforeStep,
			draw,
			hint: "Drop, drag, and squish the blob — grab anywhere and throw it to watch it jiggle and splat.",
		};
	},
};
